import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { LoadingSpinner } from "./loading-spinner";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

type ComboboxProps = {
  options: { label: string; value: string }[];
  buttonPlaceholder: string;
  searchPlaceholder?: string;
  notFoundMessage: string;
  value: string;
  setValue: (value: string) => void;
  isLoading: boolean;
  disabled: boolean;
};

export function Combobox({
  options,
  buttonPlaceholder,
  searchPlaceholder,
  notFoundMessage,
  value,
  setValue,
  isLoading,
  disabled,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={isLoading || disabled}
          aria-expanded={open}
          className="w-full justify-between truncate font-normal"
          onClick={() => setOpen(!open)}
        >
          {!isLoading ? (
            value ? (
              <div className="w-full truncate contents">{options.find(option => option.value === value)?.label}</div>
            ) : (
              buttonPlaceholder
            )
          ) : (
            <LoadingSpinner />
          )}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command
          className="p-0 truncate"
          filter={(value, search) => {
            if (!search) return 0;
            if (
              options
                .find(option => option.value === value)
                ?.label.toLowerCase()
                .includes(search.toLowerCase())
            )
              return 1;
            return 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder ?? "Search..."} className="h-9" />
          <CommandList>
            <CommandEmpty>{notFoundMessage}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  className="w-full"
                  key={option.value}
                  value={option.value}
                  onSelect={currentValue => {
                    setValue(currentValue === value ? "" : currentValue);
                    setOpen(false);
                  }}
                >
                  {option.label}
                  <Check className={cn("ml-auto", value === option.value ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
